import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./OpenLiftBenefits.css";

// La section garde le nom du composant historique pour préserver Openlift.jsx.
// Les statuts par modèle ne sont pas affichés sans validation R&D documentée.
const COPY = {
  fr: {
    pageLabel: "OPENlift",
    pageIndex: "COMPATIBILITÉ MULTIMARQUES",
    eyebrow: "Un seul parc. Plusieurs constructeurs.",
    titleFirst: "Un parc multimarques ?",
    titleSecond: "Vous n'avez pas besoin de multiplier les outils.",
    description:
      "OPENlift est conçu pour communiquer avec différentes familles de contrôleurs. La compatibilité est étudiée selon les modèles, interfaces et configurations.",
    cta: "Vérifier la compatibilité de mon parc",
    ctaNote:
      "Nous examinons les contrôleurs et les passerelles adaptés avant toute proposition.",
    gridLabel: "Exemples de constructeurs",
    gridAside: "Liste non exhaustive",
    modelNote: "Modèles à confirmer",
    footnote: "Compatibilité selon modèles et configurations.",
    reviewNote:
      "Le statut « validé / en test / sur étude » sera publié après revue technique."
  },
  en: {
    pageLabel: "OPENlift",
    pageIndex: "MULTI-BRAND COMPATIBILITY",
    eyebrow: "One fleet. Multiple manufacturers.",
    titleFirst: "A mixed-brand fleet?",
    titleSecond: "You don't need multiple tools.",
    description:
      "OPENlift is designed to communicate with different controller families. Compatibility is assessed according to controller models, interfaces and configurations.",
    cta: "Check my fleet compatibility",
    ctaNote:
      "We review controllers and suitable gateways before making a proposal.",
    gridLabel: "Examples of manufacturers",
    gridAside: "Non-exhaustive list",
    modelNote: "Models to be confirmed",
    footnote: "Compatibility depends on models and configurations.",
    reviewNote:
      "Statuses such as validated / in testing / under review will be published after technical review."
  }
};

const MAKERS = ["OTIS", "KONE", "SCHINDLER", "TKE", "ORONA", "SODIMAS"];

export default function OpenLiftBenefits() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = COPY[lang] || COPY.fr;

  const read = (key) =>
    t(`openliftMultibrand.${key}`, { defaultValue: fallback[key] });

  return (
    <section
      className="ol-multibrand"
      aria-labelledby="ol-compat-title"
      id="ol-compatibility"
    >
      <div className="ol-multibrand__container">
        <div className="ol-multibrand__topline" aria-hidden="true">
          <span className="ol-multibrand__brand">{read("pageLabel")}</span>
          <span className="ol-multibrand__index">{read("pageIndex")}</span>
        </div>

        <div className="ol-multibrand__layout">
          <div className="ol-multibrand__copy">
            <p className="ol-multibrand__eyebrow">
              <span className="ol-multibrand__eyebrow-line" aria-hidden="true" />
              {read("eyebrow")}
            </p>

            <h2 id="ol-compat-title" className="ol-multibrand__title">
              <span>{read("titleFirst")}</span>
              <span className="ol-multibrand__title-accent">
                {read("titleSecond")}
              </span>
            </h2>

            <p className="ol-multibrand__description">
              {read("description")}
            </p>

            <NavLink to="/contact" className="ol-multibrand__cta">
              <span>{read("cta")}</span>
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M4 12h16m-7-7 7 7-7 7" />
              </svg>
            </NavLink>
            <p className="ol-multibrand__cta-note">{read("ctaNote")}</p>
          </div>

          <div className="ol-multibrand__panel">
            <div className="ol-multibrand__panel-head">
              <h3 className="ol-multibrand__panel-title">{read("gridLabel")}</h3>
              <span className="ol-multibrand__panel-meta">{read("gridAside")}</span>
            </div>

            <ul className="ol-multibrand__makers" aria-label={read("gridLabel")}>
              {MAKERS.map((maker) => (
                <li className="ol-multibrand__maker" key={maker}>
                  <strong>{maker}</strong>
                </li>
              ))}
            </ul>

            <div className="ol-multibrand__notice" role="note">
              <svg
                className="ol-multibrand__notice-icon"
                viewBox="0 0 24 24"
                width="21"
                height="21"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M12 2 20 6v6c0 5-3.4 8.3-8 10-4.6-1.7-8-5-8-10V6l8-4Z" />
                <path d="m8 12 2.6 2.5L16 9" />
              </svg>
              <div>
                <p>{read("footnote")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
